import { deleteStoreItem, getAllStoreItems } from 'src/api/offline/offlineApi';
import { OfflineConstants } from 'src/api/offline/constants';
import useApi from 'src/api/useApi';
import { BrowserErrorPayload } from 'src/types/BrowserErrorPayload';

const STORE_NAME = OfflineConstants.stores.errorLog.storeName;
const STALE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface OfflineErrorLogItem {
    key?: string;
    url?: string;
    payload?: BrowserErrorPayload;
    createdAt?: number;
}

const useSyncOfflineErrorLog = () => {
    const { postApiData } = useApi();

    const syncErrorLog = async (): Promise<void> => {
        const items = (await getAllStoreItems(STORE_NAME)) as OfflineErrorLogItem[] | undefined;
        if (!items?.length) {
            return;
        }

        const now = Date.now();
        items.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));

        for (const item of items) {
            if (!item.key) {
                continue;
            }

            if ((item.createdAt ?? 0) > 0 && now - item.createdAt > STALE_MAX_AGE_MS) {
                await deleteStoreItem(STORE_NAME, item.key);
                continue;
            }

            if (!item.url || !item.payload) {
                await deleteStoreItem(STORE_NAME, item.key);
                continue;
            }

            try {
                await postApiData({
                    fullUrl: item.url,
                    data: item.payload,
                    callInBackground: true,
                    skipRefreshCacheEndpoints: true,
                });
            } catch {
                // Non-critical telemetry — delete regardless of failure type.
            }

            await deleteStoreItem(STORE_NAME, item.key);
        }
    };

    return {
        syncErrorLog,
    };
};

export default useSyncOfflineErrorLog;
